import { Col, Descriptions, Row, Space, Typography, message } from 'antd';
import axios from 'axios';
import { useState } from 'react';
import { CiLocationOn } from 'react-icons/ci';
import { RiMoneyDollarCircleLine } from 'react-icons/ri';
import { useHistory } from 'react-router-dom';

import SectionCard from '../../../components/sectionCard/SectionCard';
import StepFooter from '../../../components/stepFooter/StepFooter';
import { clearObjFields } from '../../../utils';
import { formatCurrency } from '../../../utils/format';
import OrderPreview from '../components/OrderPreview';
import useAddExpressDeliveryStore from './store';

const { Text } = Typography;

/** Descriptions-ийн ерөнхий тохиргоо: label дээр, value доор, зай багатай */
const descriptionProps = {
  size: 'small',
  colon: false,
  layout: 'vertical',
  column: { xs: 1, sm: 2 },
  labelStyle: { paddingBottom: 0 },
  contentStyle: { paddingBottom: 8 },
};

const Step2 = ({ prevStep, nextStep }) => {
  const history = useHistory();
  const {
    step1Values,
    customFieldsList,
    reference,
    products,
    deliveryTime,
    positions,
    setStep1Values,
    resetStore,
  } = useAddExpressDeliveryStore() ?? {};

  const [submitting, setSubmitting] = useState(false);

  const branch = reference?.branches?.detail?.[step1Values?.merchantCode] ?? {};
  const paymentType = reference?.contract?.data?.payment; // postPay төлбөрөө дараа төлөж болох эсэх
  const getPickUpAddress = (fieldName, deliveryPositionCode) => {
    const field = customFieldsList?.[fieldName]?.find((item) =>
      item.enum?.includes(deliveryPositionCode)
    );
    if (field) {
      const address = field.enumDictionary[`${deliveryPositionCode}`];
      return address?.addressName;
    }
    return '';
  };
  const deliveryType = step1Values?.deliveryTime;

  const isExpress = deliveryType === 'express';

  const createOrder = () => {
    let body = {
      branchCode: step1Values?.merchantCode,
      deliveryOrders: [],
      source: '',
      deliveryType: deliveryTime,
      type: deliveryType,
      contractType: step1Values?.contractType[0],
    };
    if (step1Values?.orders?.length > 0) {
      //Багц шуурхай хүргэлт эсэх
      body.deliveryOrders = step1Values?.orders?.map((item, i) => {
        // TO-DO
        const product = products?.find((product) => product?.attributes?.size === item?.size);
        item.deliveryType = 'delivery';
        const { phoneNumber, firstName, mail, ...restFields } = item || {};
        const res = {
          receiverPhone: item?.phoneNumber,
          receiverName: item?.firstName,
          receiverMail: item?.mail,
          ...(isExpress && { days: 1 }),
          lastStationAddress: getLastStationAddress(item, i),
          productSku: product?.productSku,
          ...restFields,
        };

        // delete res.address;
        delete res.aimagHot;
        delete res.sumDuureg;
        delete res.bagHoroo;
        delete res.deliveryPositionCode;
        return clearObjFields(res);
      });
      /*
        direct-delivery дээр захиалга үүсгэхдээ шуурхай болон энгийн
        хүргэлтэн дээр тусдаа api дуудаж байгаа. Энгийн шуурхай байна уу эсвэл
        багц шуурхай байна уу хамаагүй `/api/v1.0/request/expressOrderCreate` энэ api-г
        дуудна.
      */

      const orderCreateUrl = !isExpress
        ? '/api/v1.0/request/create-direct-delivery-request'
        : '/api/v1.0/request/expressOrderCreate';

      setSubmitting(true);
      axios
        .post(orderCreateUrl, body)
        .then(({ data }) => {
          const result = data?.result;

          if (data?.statusCode === 200) {
            message.success('Захиалга амжилттай үүслээ.');
            if (isExpress) {
              setStep1Values({
                ...step1Values,
                payment: result.invoice,
              });
              if (paymentType === 'postPay') {
                history.push('/merchant/order/delivery');
                resetStore();
              } else {
                nextStep();
              }
            } else {
              setStep1Values({
                ...step1Values,
                payment: paymentType === 'postPay' ? result.invoice : result,
              });
              if (paymentType === 'postPay') {
                history.push('/merchant/order/delivery');
                resetStore();
              } else {
                nextStep();
              }
            }
          }
        })
        .catch((e) => {
          // Мэдэгдлийг axios-ийн global interceptor гаргана — энд давхардуулахгүй
          console.error('Order create error: ', e);
        })
        .finally(() => setSubmitting(false));
    }
  };

  const getLastStationAddress = (item, index) => {
    const position = Array.isArray(positions[index]) ? positions[index]?.map(String) : [];
    if (['delivery', 'expressdelivery'].includes(item?.deliveryType)) {
      return {
        address: item.address,
        addressName: item?.address?.map((el) => el?.name ?? '')?.join(', ') ?? '',
        addressDetails: item?.deliveryDetail ?? '',
        position,
      };
    }

    if (['deliverytocabinet', 'deliverytohub'].includes(item?.deliveryType)) {
      return {
        address: item?.deliveryPositionCode,
        addressName: getPickUpAddress(index, item.deliveryPositionCode),
        addressDetails: getPickUpAddress(index, item.deliveryPositionCode),
        position,
      };
    }

    return {};
  };

  const {
    totalPrice,
    totalDeliveryPrice,
    totalExecutionPrice,
    totalExpressPrice,
    totalExpressExceededPrice,
  } = step1Values;

  const priceItems = isExpress
    ? [
        {
          key: 'express',
          label: 'Шуурхай хүргэлтийн төлбөр',
          children: formatCurrency(totalExpressPrice === 0 ? totalPrice : totalExpressPrice || 0),
        },
        {
          key: 'exceeded',
          label: '2км -ээс хэтэрсэн нэмэлт төлбөр',
          children: formatCurrency(totalExpressExceededPrice || 0),
        },
        {
          key: 'total',
          label: 'Нийт төлбөр',
          children: <Text strong>{formatCurrency(totalPrice || 0)}</Text>,
        },
      ]
    : [
        {
          key: 'execution',
          label: 'Захиалга гүйцэтгэлийн төлбөр',
          children: formatCurrency(totalExecutionPrice || 0),
        },
        {
          key: 'delivery',
          label: 'Хүргэлтийн төлбөр',
          children: formatCurrency(totalDeliveryPrice || 0),
        },
        {
          key: 'total',
          label: 'Нийт төлбөр',
          children: <Text strong>{formatCurrency(totalPrice || 0)}</Text>,
        },
      ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {/* Хоёр карт нэг мөрөнд — жижиг дэлгэц дээр доош бөхнө */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <SectionCard
            icon={<RiMoneyDollarCircleLine />}
            title="Төлбөрийн мэдээлэл"
            style={{ height: '100%' }}
          >
            <Descriptions
              {...descriptionProps}
              column={{ xs: 1, sm: 2, lg: priceItems.length }}
              items={priceItems}
            />
          </SectionCard>
        </Col>

        <Col xs={24} lg={12}>
          <SectionCard
            icon={<CiLocationOn />}
            title="Бараа татах салбар"
            style={{ height: '100%' }}
          >
            <Descriptions
              {...descriptionProps}
              items={[
                {
                  key: 'deliveryTime',
                  label: 'Хүргэлтийн хугацаа',
                  children: isExpress ? 'Шуурхай' : 'Энгийн',
                },
                {
                  key: 'branch',
                  label: 'Бараа татах салбар',
                  children: branch?.name ?? '-',
                },
              ]}
            />
          </SectionCard>
        </Col>
      </Row>

      {step1Values?.orders?.length > 0 &&
        step1Values.orders.map((item, index) => {
          item.priceDetail = step1Values?.result?.[index] || {};
          return <OrderPreview key={index} index={index} order={item} />;
        })}

      <StepFooter
        onPrev={prevStep}
        onSubmit={createOrder}
        htmlType="button"
        submitText="Захиалга үүсгэх"
        loading={submitting}
        extra={
          <Text type="secondary">
            Нийт төлбөр: <Text strong>{formatCurrency(totalPrice || 0)}</Text>
          </Text>
        }
      />
    </Space>
  );
};

export default Step2;
